export const getValidDobAndReferenceDate = (
  dob: string,
  referenceDate: string | Date | null
): { dobDate: Date; refDate: Date } | null => {
  if (typeof dob !== 'string' || !isValidNemsisDOB(dob)) return null
  if (typeof referenceDate !== 'string' || !isValidZollNowDate(referenceDate)) return null

  const dobDate = parseDateForAge(dob)
  const refDate = parseDateForAge(referenceDate)
  if (!dobDate || isNaN(dobDate.getTime())) return null
  if (!refDate || isNaN(refDate.getTime())) return null
  if (refDate.getTime() === dobDate.getTime()) return null
  return { dobDate, refDate }
}

const MMDDYYYY = /^\d{2}\/\d{2}\/\d{4}$/
const MMDDYY = /^\d{2}\/\d{2}\/\d{2}$/

export const MIN_NEMSIS_DATE = new Date(1890, 0, 1)
export const MAX_NEMSIS_DATE = new Date(2050, 0, 1)

const MIN_ZOLL_NOW_DATE = new Date(1950, 0, 1)
const MAX_ZOLL_NOW_DATE = new Date(2050, 0, 1)

export const getDateTime = (): string => {
  const date = new Date()
  return date.toISOString()
}

export const getCurrentTime = (): string => {
  const date = new Date()
  return `${date.getHours() < 10 ? '0' + date.getHours() : date.getHours()}:${
    date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes()
  }:${date.getSeconds() < 10 ? '0' + date.getSeconds() : date.getSeconds()}`
}

export const getCurrentYear = () => {
  return new Date().getFullYear()
}

export const formartDate = (date: string): string => {
  const dateArray = date.split('/')
  const month = +dateArray[0]
  const day = +dateArray[1]
  const year = +dateArray[2] % 100
  return `${month < 10 ? '0' + month : month}/${day < 10 ? '0' + day : day}/${year}`
}

export const formatTime = (time: string): string => {
  const timeArray = time.split(':')
  let hours = +timeArray[0]
  const minutes = +timeArray[1]
  if (time.includes('PM') && hours < 12) hours = hours + 12
  if (time.includes('AM') && hours === 12) hours = hours - 12
  return `${hours < 10 ? '0' + hours : hours}:${minutes < 10 ? '0' + minutes : minutes}`
}

export const getCurrentDate = (getYearFull = false): string => {
  const date = new Date()
  const month = date.getMonth() + 1
  const day = date.getDate()
  const year = getYearFull ? date.getFullYear() : date.getFullYear() % 100
  return `${month < 10 ? '0' + month : month}/${day < 10 ? '0' + day : day}/${year}`
}

export const getFormattedDate = (date: Date): string => {
  const month = date.getMonth() + 1
  const day = date.getDate()
  const year = date.getFullYear() % 100
  return `${month < 10 ? '0' + month : month}/${day < 10 ? '0' + day : day}/${year}`
}

export const getFormattedTime = (date: Date): string => {
  return `${date.getHours() < 10 ? '0' + date.getHours() : date.getHours()}:${
    date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes()
  }`
}

const toValidDate = (value: string): Date | null => {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export const getFormattedTimeWithSeconds = (date: Date): string => {
  return `${date.getHours() < 10 ? '0' + date.getHours() : date.getHours()}:${
    date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes()
  }:${date.getSeconds() < 10 ? '0' + date.getSeconds() : date.getSeconds()}`
}

export const transformTime = (time: string, displaySeconds?: boolean): string => {
  const militaryTimeRegex = displaySeconds ? /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/ : /^([01]\d|2[0-3]):([0-5]\d)$/
  if (militaryTimeRegex.test(time) && !displaySeconds) return `${time}:00`
  if (militaryTimeRegex.test(time)) return time
  if (!time) return ''

  time = time.replaceAll(':', '')

  if (isNaN(+time)) return ''

  const timeParts = time.split('')
  switch (timeParts.length) {
    case 1:
      return time
    case 2:
      return `${timeParts[0]}${timeParts[1]}:`
    case 3:
      return `${timeParts[0]}${timeParts[1]}:${timeParts[2]}`
    case 4:
      return displaySeconds
        ? `${timeParts[0]}${timeParts[1]}:${timeParts[2]}${timeParts[3]}`
        : `${timeParts[0]}${timeParts[1]}:${timeParts[2]}${timeParts[3]}`
    case 5:
      return displaySeconds ? `${timeParts[0]}${timeParts[1]}:${timeParts[2]}${timeParts[3]}:${timeParts[4]}` : ''
    case 6:
      return displaySeconds
        ? `${timeParts[0]}${timeParts[1]}:${timeParts[2]}${timeParts[3]}:${timeParts[4]}${timeParts[5]}`
        : ''
    default:
      return ''
  }
}

export const transformDate = (date: string): string => {
  const MMDDYY = /^\d{2}\/\d{2}\/\d{2}$/
  if (MMDDYY.test(date)) return date
  if (!date) return ''

  date = date.replace('/', '').replace('/', '')

  if (isNaN(+date)) return ''

  const dateParts = date.split('')
  if (dateParts) {
    switch (dateParts.length) {
      case 1:
        return date
      case 2:
        return `${dateParts[0]}${dateParts[1]}/`
      case 3:
        return `${dateParts[0]}${dateParts[1]}/${dateParts[2]}`
      case 4:
        return `${dateParts[0]}${dateParts[1]}/${dateParts[2]}${dateParts[3]}/`
      case 5:
        return `${dateParts[0]}${dateParts[1]}/${dateParts[2]}${dateParts[3]}/${dateParts[4]}`
      case 6:
        return `${dateParts[0]}${dateParts[1]}/${dateParts[2]}${dateParts[3]}/${dateParts[4]}${dateParts[5]}`
      default:
        return ''
    }
  }
  return ''
}

export const transformDateWith4DigitYear = (date: string): string => {
  if (MMDDYYYY.test(date)) {
    return date
  }

  if (!date) {
    return ''
  }

  date = date.replace(/\//g, '')

  if (isNaN(+date)) {
    return ''
  }

  if (date.length < 3) {
    return date
  } else if (date.length >= 3 && date.length <= 4) {
    return date.substring(0, 2) + '/' + date.substring(2)
  } else {
    return date.substring(0, 2) + '/' + date.substring(2, 4) + '/' + date.substring(4, 8)
  }
}

export const isValidNemsisDate = (dateStr: string): boolean => {
  if (!dateStr) {
    return false
  }

  if (!MMDDYYYY.test(dateStr)) {
    return false
  }

  const date = new Date(dateStr)
  return date >= MIN_NEMSIS_DATE && date <= MAX_NEMSIS_DATE
}

export const isValidNemsisDOB = (dateStr: string): boolean => {
  if (!dateStr) {
    return false
  }

  if (!MMDDYYYY.test(dateStr)) {
    return false
  }

  const date = new Date(dateStr)
  return date >= MIN_NEMSIS_DATE && date <= MAX_NEMSIS_DATE
}

export const isValidZollNowDate = (dateStr: string): boolean => {
  if (!dateStr) {
    return false
  }

  if (!MMDDYY.test(dateStr)) {
    return false
  }

  const date = new Date(dateStr)
  return date >= MIN_ZOLL_NOW_DATE && date <= MAX_ZOLL_NOW_DATE
}

export const isValidZollTime = (timeStr: string): boolean => {
  if (!timeStr) {
    return false
  }
  const militaryTimeRegex = /^([01]\d|2[0-3]):([0-5]\d)(:([0-5]\d))?$/
  return militaryTimeRegex.test(timeStr)
}

export const transformDriversLicenseDate = (date: string): string => {
  const dp = date.split('')
  return `${dp[0]}${dp[1]}/${dp[2]}${dp[3]}/${dp[4]}${dp[5]}${dp[6]}${dp[7]}`
}

/**
 * Parses a date string in MM/DD/YY or MM/DD/YYYY format to a Date (date-only, no time).
 * Used for DOB and dispatched date so age can be calculated as (reference date - DOB).
 */
export const parseDateForAge = (dateStr: string): Date | null => {
  if (!dateStr || typeof dateStr !== 'string') return null
  const parts = dateStr.trim().split('/')
  if (parts.length !== 3) return null
  if (!/^\d{2}$|^\d{4}$/.test(parts[2])) return null
  const month = parseInt(parts[0], 10) - 1
  const day = parseInt(parts[1], 10)
  let year = parseInt(parts[2], 10)
  if (isNaN(month) || isNaN(day) || isNaN(year)) return null
  if (parts[2].length === 2) {
    year = year < 50 ? 2000 + year : 1900 + year
  }
  const d = new Date(year, month, day)
  if (isNaN(d.getTime())) return null
  return d
}

export const calculateAge = (date: string): any => {
  const now: Date = new Date()

  const yearNow: number = now.getFullYear()
  const monthNow: number = now.getMonth()
  const dateNow: number = now.getDate()

  const dob: Date = new Date(date)

  const yearDob: number = dob.getFullYear()
  const monthDob: number = dob.getMonth()
  const dateDob: number = dob.getDate()

  let yearAge: number = yearNow - yearDob
  let monthAge: number

  if (monthNow >= monthDob) {
    monthAge = monthNow - monthDob
  } else {
    yearAge--
    monthAge = 12 + monthNow - monthDob
  }

  let dateAge: number
  if (dateNow >= dateDob) {
    dateAge = dateNow - dateDob
  } else {
    monthAge--
    dateAge = 31 + dateNow - dateDob

    if (monthAge < 0) {
      monthAge = 11
      yearAge--
    }
  }

  return {
    years: yearAge,
    months: monthAge,
    days: dateAge,
  }
}

/**
 * Calculate age from DOB using a reference date (e.g. Dispatched Date / encounter date).
 * Returns { years, months, days }. When reference date equals DOB, returns 0 years, 0 months, 0 days.
 * Age units are only Days, Months, Years (no Hours).
 * If reference is before DOB or invalid or null, returns 0, 0, 0.
 */
export const calculateAgeFromReference = (
  dob: string,
  referenceDate: string | Date | null | undefined
): { years: number; months: number; days: number } => {
  const dobDate = typeof dob === 'string' ? parseDateForAge(dob) : null
  if (!dobDate || isNaN(dobDate.getTime())) {
    return { years: 0, months: 0, days: 0 }
  }

  let ref: Date
  if (referenceDate == null || referenceDate === '') {
    return { years: 0, months: 0, days: 0 }
  } else if (typeof referenceDate === 'string') {
    const parsed = parseDateForAge(referenceDate)
    if (!parsed || isNaN(parsed.getTime())) return { years: 0, months: 0, days: 0 }
    else ref = parsed
  } else {
    return { years: 0, months: 0, days: 0 }
  }

  const yearRef = ref.getFullYear()
  const monthRef = ref.getMonth()
  const dateRef = ref.getDate()
  const yearDob = dobDate.getFullYear()
  const monthDob = dobDate.getMonth()
  const dateDob = dobDate.getDate()

  if (ref.getTime() < dobDate.getTime()) {
    return { years: 0, months: 0, days: 0 }
  }

  let yearAge = yearRef - yearDob
  let monthAge: number
  if (monthRef >= monthDob) {
    monthAge = monthRef - monthDob
  } else {
    yearAge--
    monthAge = 12 + monthRef - monthDob
  }

  let dateAge: number
  if (dateRef >= dateDob) {
    dateAge = dateRef - dateDob
  } else {
    monthAge--
    dateAge = 31 + dateRef - dateDob
    if (monthAge < 0) {
      monthAge = 11
      yearAge--
    }
  }

  return {
    years: Math.max(0, yearAge),
    months: Math.max(0, monthAge),
    days: Math.max(0, dateAge),
  }
}

export const getAge = (date: string): string => {
  const age = calculateAge(date)
  if (age.years === 0 && age.months === 0) {
    return age.days + ''
  }
  if (age.years === 0) {
    return age.months + ''
  }
  return age.years + ''
}

/**
 * Get age value (number as string) from DOB using reference date (e.g. Dispatched Date).
 * When referenceDate is null/invalid (not in zoll date format MM/DD/YY), or DOB is null/undefined/invalid,
 * or DOB === referenceDate, returns ''.
 * Only Days, Months, Years (no Hours).
 */
export const getAgeFromReference = (dob: string, referenceDate: string | Date | null): string => {
  const valid = getValidDobAndReferenceDate(dob, referenceDate)
  if (!valid) return ''

  const age = calculateAgeFromReference(dob, referenceDate)

  if (age.years === 0 && age.months === 0 && age.days === 0) {
    return ''
  }
  if (age.years === 0 && age.months === 0) {
    return age.days + ''
  }
  if (age.years === 0) {
    return age.months + ''
  }
  return age.years + ''
}

enum AgeUnit {
  Years = 'y',
  Months = 'm',
  Days = 'd',
}
export const getAgeUnit = (date: string): string => {
  const age = calculateAge(date)
  if (age.years === 0 && age.months === 0) {
    return AgeUnit.Days
  }
  if (age.years === 0) {
    return AgeUnit.Months
  }
  return AgeUnit.Years
}

/**
 * Get age unit option id (d, m, or y) from DOB using reference date.
 * Only Days, Months, Years (no Hours). When referenceDate is null/undefined, uses current date.
 */
export const getAgeUnitFromReference = (dob: string, referenceDate: string | Date | null): string => {
  const valid = getValidDobAndReferenceDate(dob, referenceDate)
  if (!valid) return ''

  const age = calculateAgeFromReference(dob, referenceDate)

  if (age.years === 0 && age.months === 0 && age.days === 0) {
    return ''
  }
  if (age.years === 0 && age.months === 0) {
    return AgeUnit.Days
  }
  if (age.years === 0) {
    return AgeUnit.Months
  }
  return AgeUnit.Years
}

enum AgeUnitOptionId {
  Years = 'y',
  Months = 'm',
  Days = 'd',
}
export const getAgeUnitOptionId = (date: string): string => {
  const age = calculateAge(date)
  if (age.years === 0 && age.months === 0) {
    return AgeUnitOptionId.Days
  }
  if (age.years === 0) {
    return AgeUnitOptionId.Months
  }
  return AgeUnitOptionId.Years
}

/**
 * Get age unit option id from DOB using reference date (e.g. Dispatched Date).
 * Only Days, Months, Years. When referenceDate is null/undefined, uses current date.
 */
export const getAgeUnitOptionIdFromReference = (dob: string, referenceDate: string | Date | null): string => {
  return getAgeUnitFromReference(dob, referenceDate)
}

export const getCurrentTimeFormatted = (): string => {
  const currentTime = new Date()
  const hours = currentTime.getHours()
  const minutes = currentTime.getMinutes()
  const seconds = currentTime.getSeconds()
  const milliseconds = currentTime.getMilliseconds()
  return `${hours}:${minutes}:${seconds}.${milliseconds}`
}

export const transformDateTimeToMonthDayYear = (dateTime: string): string => {
  const dt: Date = new Date(dateTime)
  const month = dt.getMonth() + 1
  const day = dt.getDate()
  return `${+month < 10 ? '0' : ''}${month}/${+day < 10 ? '0' : ''}${day}/${dt.getFullYear()}`
}

export const getCurrentDateTimeByTimeZone = (timeZone: string): string => {
  const date = new Date()
  const dateTime = date.toLocaleString('en-US', { timeZone })
  if (dateTime && dateTime.indexOf(',') > -1) {
    const datetimeArray = dateTime.split(', ')
    const date = datetimeArray[0]
    const time = datetimeArray[1]
    const dateFormatted = formartDate(date)
    const timeFormatted = formatTime(time)
    return dateFormatted + ' ' + timeFormatted
  }
  return ''
}

export const getCurrentDateTimeHH24FormatByTimeZone = (timeZone: string, inputDate?: string | Date): string => {
  const date = inputDate ? new Date(inputDate) : new Date()
  const dateTime = date.toLocaleString('en-US', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: timeZone,
  })
  let formattedDateTime = ''
  if (dateTime) {
    formattedDateTime = dateTime.replace(', 24:', ', 00:')
    formattedDateTime = formattedDateTime.replace(',', '')
  }
  return formattedDateTime
}

export const convertSecondsToHours = (seconds: number) => {
  return seconds / (1000 * 60 * 60)
}

export const convertSecondsToMin = (seconds: number) => {
  return seconds / (1000 * 60)
}

export const truncateSecondsIfNecessary = (timeString: string, shouldDisplaySeconds: boolean) => {
  if (!shouldDisplaySeconds) {
    const containsSecondsRegex = /(\d{2}:\d{2}):\d*$/
    const containsSeconds = containsSecondsRegex.test(timeString)

    if (containsSeconds) {
      const match = timeString.match(containsSecondsRegex)
      if (match && match.length > 1) {
        return match[1]
      }
    }
  }
  return timeString
}

export const addMinutesToDate = (date: Date, minutesToAdd: number) => {
  const updatedDate = new Date(date)
  updatedDate.setMinutes(updatedDate.getMinutes() + minutesToAdd)
  return updatedDate
}

export const subtractMinutesToDate = (date: Date, minutesToSubtract: number) => {
  const updatedDate = new Date(date)
  updatedDate.setMinutes(updatedDate.getMinutes() - minutesToSubtract)
  return updatedDate
}

export const getMidpointDate = (date1: Date, date2: Date): Date => {
  const timestamp1 = new Date(date1).getTime()
  const timestamp2 = new Date(date2).getTime()
  const midpointTimestamp = (timestamp1 + timestamp2) / 2
  const midpointDate = new Date(midpointTimestamp)
  return midpointDate
}

export const getTimeDifferenceInMinutes = (startDate: Date, endDate: Date): number => {
  const diffMilliseconds = new Date(endDate).getTime() - new Date(startDate).getTime()
  return Math.floor(diffMilliseconds / (1000 * 60))
}

export const formatDateTime = (isoString: string): string => {
  const date = new Date(isoString)
  const formattedDate = getFormattedDate(date)
  const formattedTime = getFormattedTime(date)
  return `${formattedDate} ${formattedTime}`
}

export const formatShortMonthDateTime = (isoString: string): string => {
  const date = toValidDate(isoString)
  if (!date) return isoString

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

export const getCurrentDateTime = (): string => {
  return getCurrentDate(true) + ' ' + getCurrentTime().slice(0, -3)
}

export const getFormattedTimezone = (timezoneOffset: number) => {
  const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60)
  const offsetMinutes = Math.abs(timezoneOffset) % 60
  const sign = timezoneOffset > 0 ? '-' : '+'
  const formattedOffset = `${sign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes
    .toString()
    .padStart(2, '0')}`
  return formattedOffset.toString()
}

/**
 * Append ":00" seconds if time is in HH:MM format (even if invalid) and displaySeconds is true.
 * Returns original time if:
 * - displaySeconds is false
 * - time is already HH:MM:SS
 * - input doesn't match expected formats
 *
 * @param time - input time string
 * @param displaySeconds - flag to append seconds
 * @returns formatted time string
 */

export const appendSeconds = (time: string, displaySeconds: boolean): string => {
  if (!displaySeconds || !time) return time

  const normalizedTime = time.trim().replace(/[\-.\s\\]/g, ':')

  const hhmmssFormat = /^\d{1,2}:\d{2}:\d{2}$/
  const hhmmFormat = /^\d{1,2}:\d{2}$/

  if (hhmmssFormat.test(normalizedTime)) return normalizedTime
  if (hhmmFormat.test(normalizedTime)) return `${normalizedTime}:00`

  return time
}

export const isTodaysDate = (dateString: string): boolean => {
  if (!dateString) return false

  const today = getCurrentDate(true)
  return dateString === today
}

export const isFutureDate = (dateString: string): boolean => {
  if (!dateString) return false

  const today = getCurrentDate(true)
  const todayDate = new Date(today)
  const checkDate = new Date(dateString)

  return checkDate > todayDate
}

export const isTodayOrFuture = (dateString: string): boolean => {
  return isTodaysDate(dateString) || isFutureDate(dateString)
}

export const removeSeconds = (timeString: string): string => {
  return timeString.replace(/(:\d+)(:\d+)$/, '$1:00')
}
